import { decodeJwt, isExpired, type JwtClaims } from './jwt'

/**
 * Where the admin's JWT lives for the life of the browser session.
 *
 * `sessionStorage`, not `localStorage`: an operations console on a shared or
 * unattended machine should not leave a 30-day admin token on disk after the
 * tab is closed. The cost is re-authenticating in a new tab, which is the right
 * trade for this surface.
 */

const STORAGE_KEY = 'dfm.admin.session'

export interface AdminSession {
  token: string
  userId: number
  name: string
  email: string | null
  phone: string | null
  role: string
  profileImageUrl: string | null
}

type Listener = (session: AdminSession | null) => void

const listeners = new Set<Listener>()
let cached: AdminSession | null | undefined

function readStorage(): AdminSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null

    const session = parsed as AdminSession
    if (typeof session.token !== 'string' || session.token.length === 0) return null

    // A stored token that has already run out is the same as no session.
    if (isExpired(decodeJwt(session.token))) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export function getSession(): AdminSession | null {
  if (cached === undefined) {
    cached = readStorage()
  }
  return cached
}

export function getToken(): string | null {
  return getSession()?.token ?? null
}

export function getClaims(): JwtClaims | null {
  const token = getToken()
  return token ? decodeJwt(token) : null
}

export function setSession(session: AdminSession | null): void {
  cached = session
  try {
    if (session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Storage can be unavailable (private mode, quota). The in-memory copy
    // still carries the session for as long as the page is open.
  }
  listeners.forEach((listener) => listener(session))
}

export function clearSession(): void {
  setSession(null)
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Test seam: drops the in-memory copy so the next read hits storage again. */
export function resetSessionCache(): void {
  cached = undefined
}
